import { useMutation } from '@apollo/client';
import KeyIcon from '@assets/svgs/key.svg';
import PasswordInput from '@components/Inputs/PasswordInput';
import { PageHead } from '@components/PageHead';
import { UPDATE_PASSWORD_FORGOT } from '@gql/auth';
import { Box, Button, Typography } from '@mui/material';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import './index.css';

export const FirstLoginResetPasswordPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const { login: authLogin } = useAuth();

    const email = searchParams.get('email') || '';
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordsMatch, setPasswordsMatch] = useState(true);

    const [updatePasswordForgot, { loading: resettingPassword }] = useMutation(UPDATE_PASSWORD_FORGOT, {
        onCompleted: (data) => {
            if (data?.updatePasswordForgot) {
                // Update auth context with new login data
                const loginData = data.updatePasswordForgot;
                authLogin(loginData);

                // Store token in localStorage for persistence
                localStorage.setItem('authToken', loginData.token);
                localStorage.setItem('authUser', JSON.stringify(loginData));

                showSnackbar(
                    'Mot de passe réinitialisé avec succès ! Vous êtes maintenant connecté.',
                    'success'
                );

                // Navigate to dashboard
                setTimeout(() => {
                    navigate('/');
                }, 500);
            }
        },
        onError: (error) => {
            const errorMessage = error.message;
            if (errorMessage.includes('invalid') || errorMessage.includes('invalide')) {
                showSnackbar(
                    'Mot de passe invalide. Veuillez réessayer.',
                    'error'
                );
            } else {
                showSnackbar(
                    errorMessage || 'Échec de la réinitialisation du mot de passe. Veuillez réessayer.',
                    'error'
                );
            }
        },
    });

    useEffect(() => {
        // Check if email is provided
        if (!email) {
            navigate('/auth/login');
        }
        // Verify if passwords match
        if (confirmPassword) {
            setPasswordsMatch(newPassword === confirmPassword);
        }
    }, [newPassword, confirmPassword, email, navigate]);

    const validatePassword = (password: string): boolean => {
        // Requirements:
        // - At least 8 characters
        // - Contains uppercase letter
        // - Contains lowercase letter
        // - Contains number
        // - Contains special character
        const minLength = password.length >= 8;
        const hasUppercase = /[A-Z]/.test(password);
        const hasLowercase = /[a-z]/.test(password);
        const hasNumber = /\d/.test(password);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

        return minLength && hasUppercase && hasLowercase && hasNumber && hasSpecialChar;
    };

    const handleSubmit = async () => {
        // Validate form
        if (!newPassword) {
            showSnackbar('Veuillez entrer un nouveau mot de passe', 'warning');
            return;
        }

        if (!validatePassword(newPassword)) {
            showSnackbar('Le mot de passe ne respecte pas les exigences', 'warning');
            return;
        }

        if (newPassword !== confirmPassword) {
            showSnackbar('Les mots de passe ne correspondent pas', 'warning');
            return;
        }

        try {
            await updatePasswordForgot({
                variables: {
                    resetPasswordInput: {
                        email: email.toLowerCase(),
                        newPassword: newPassword,
                    },
                },
            });
        } catch (err) {
            // Error handled by onError callback
        }
    };

    const isFormValid =
        newPassword.length > 0 &&
        validatePassword(newPassword) &&
        passwordsMatch;

    return (
        <>
            <PageHead useDynamicTitle />
            <Box className="first-login-reset-password-container">
                {/* Key icon */}
                <Box className="key-icon-wrapper">
                    <i className='fi fi-br-key' style={{ fontSize: '24px' }}></i>
                </Box>

                {/* Title and subtitle */}
                <Box className="header-section">
                    <Typography variant="h1" className="main-heading">
                        Créer votre mot de passe
                    </Typography>
                    <Typography variant="body1" className="subtitle">
                        Pour des raisons de sécurité, veuillez créer un nouveau mot de passe pour accéder à votre compte.
                    </Typography>
                </Box>

                {/* Form */}
                <form className="reset-form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                    {/* New password field */}
                    <Box className="input-group">
                        <PasswordInput
                            label={
                                <span className="input-label">
                                    Nouveau mot de passe<span className="required-asterisk">*</span>
                                </span>
                            }
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Entrez votre nouveau mot de passe"
                            className="custom-input"
                            disabled={resettingPassword}
                            error={newPassword.length > 0 && !validatePassword(newPassword)}
                            helperText={
                                newPassword.length > 0 && !validatePassword(newPassword)
                                    ? 'Le mot de passe ne respecte pas les exigences'
                                    : ''
                            }
                            fullWidth
                        />
                    </Box>

                    {/* Confirm password field */}
                    <Box className="input-group">
                        <PasswordInput
                            label={
                                <span className="input-label">
                                    Confirmer le mot de passe<span className="required-asterisk">*</span>
                                </span>
                            }
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirmez votre nouveau mot de passe"
                            className="custom-input"
                            disabled={resettingPassword}
                            error={confirmPassword.length > 0 && !passwordsMatch}
                            helperText={
                                confirmPassword.length > 0 && !passwordsMatch
                                    ? 'Les mots de passe ne correspondent pas'
                                    : ''
                            }
                            fullWidth
                        />
                    </Box>

                    {/* Password requirements */}
                    <Box className="password-requirements">
                        <Typography variant="body2" className="requirements-title">
                            Exigences du mot de passe :
                        </Typography>
                        <ul className="requirements-list">
                            <li className={newPassword.length >= 8 ? 'valid' : 'invalid'}>
                                Au moins 8 caractères
                            </li>
                            <li className={/[A-Z]/.test(newPassword) ? 'valid' : 'invalid'}>
                                Une lettre majuscule
                            </li>
                            <li className={/[a-z]/.test(newPassword) ? 'valid' : 'invalid'}>
                                Une lettre minuscule
                            </li>
                            <li className={/\d/.test(newPassword) ? 'valid' : 'invalid'}>
                                Un chiffre
                            </li>
                            <li className={/[!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? 'valid' : 'invalid'}>
                                Un caractère spécial
                            </li>
                        </ul>
                    </Box>

                    {/* Submit button */}
                    <button
                        type="submit"
                        disabled={!isFormValid || resettingPassword}
                        className="submit-button"
                    >
                        {resettingPassword ? 'Création en cours...' : 'Créer mon mot de passe'}
                    </button>
                </form>

                {/* Security notice */}
                <Typography variant="body2" className="security-notice">
                    Pour des raisons de sécurité, nous ne stockons PAS votre mot de passe. Soyez assuré que nous ne vous enverrons jamais votre mot de passe par e-mail. Pour plus d'informations, vous pouvez nous contacter à{' '}
                    <a href="mailto:support@upercut.fr" className="support-link">
                        support@upercut.fr
                    </a>
                    .
                </Typography>
            </Box>
        </>
    );
};
